// @flow
import React, {useRef, useState} from 'react';
import {InputBase, InputInlay} from 'sail/Input';
import {CompactTooltip} from 'sail/Tooltip';
import ContextualPopover from 'sail/Layer/ContextualPopover';
import {BodyMono, CaptionMono} from 'sail/Text';
import TruncatedText from 'sail/TruncatedText';
import Button from 'sail/Button';
import Group from 'sail/Group';
import Box from 'sail/Box';
import {ArrayElementInput} from './ArrayElementInput';
import {arrayItemWidth, addRemoveButtonWidth} from './styleConstants';
import {dimensions} from '../../StyleHelpers';
import {useApiExplorer} from '../../ApiExplorerProvider';

type ArrayInputBaseProps = {|
  field_id: string,
  param_key: string,
  required: boolean,
  description: string,
  param: Object,
|};

const deleteEntry = (param_key, ind, params, setParams) => {
  const {[param_key]: oldArray, ...otherParams} = params;
  if (oldArray) {
    oldArray.splice(ind, 1); // remove element at specified index
    setParams({...otherParams, [param_key]: [...oldArray]});
  }
};

export const ArrayInputBase = ({
  field_id,
  param_key,
  required,
  description,
  param,
}: ArrayInputBaseProps) => {
  const buttonRef = useRef<?HTMLSpanElement>(null);
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const {
    state: {params},
    setParams,
  } = useApiExplorer();

  const context = (
    <React.Fragment>
      <span ref={buttonRef}>
        <InputBase width={dimensions.width} radius="none">
          <Button
            onClick={() => {
              setShowPopover(true);
            }}
            icon="edit"
            iconPosition="right"
            label={
              <div style={{width: 300}}>
                <TruncatedText>
                  {params[param_key] && params[param_key].length > 0
                    ? `[${params[param_key].join(', ')}]`
                    : ''}
                </TruncatedText>
              </div>
            }
            radius="none"
            width="maximized"
            size="large"
          />
          <InputInlay buffer="horizontal" size="large">
            <CaptionMono color="gray">array</CaptionMono>
          </InputInlay>
        </InputBase>
      </span>
      {buttonRef.current && (
        <ContextualPopover
          context={{element: buttonRef.current}}
          contextMeetingPoint="bottom"
          layerMeetingPoint="top"
          shown={showPopover}
          onClickOutside={() => {
            setShowPopover(false);
            return 'handled';
          }}
        >
          <Box
            direction="vertical"
            divider={{side: 'all'}}
            style={{margin: '8px'}}
          >
            {params[param_key] &&
              params[param_key].map((entry, ind) => {
                return (
                  <Group direction="horizontal">
                    <Box
                      style={{width: arrayItemWidth}}
                      divider={{side: 'bottom'}}
                      padding={{vertical: 4}}
                    >
                      <BodyMono align="center">{entry}</BodyMono>
                    </Box>
                    <Button
                      icon="cancel"
                      label=""
                      onClick={() => {
                        deleteEntry(param_key, ind, params, setParams);
                      }}
                      width={addRemoveButtonWidth}
                      radius="none"
                    />
                  </Group>
                );
              })}
            <ArrayElementInput
              field_id={field_id}
              param_key={param_key}
              elementType={param.input_type.elementType}
              params={params}
              setParams={setParams}
            />
          </Box>
        </ContextualPopover>
      )}
    </React.Fragment>
  );

  return description ? (
    <CompactTooltip context={context} label={description} position="right" />
  ) : (
    context
  );
};
